import React, { useMemo, useState } from "react";
import { HiOutlineChevronDown } from "react-icons/hi2";
import InputField from "components/common/Input/Input";
import SelectOption from "components/common/Select/Select";
import { TimesheetCodeAllocationSectionCard } from "../../../components/TimesheetRevamp/TimesheetCodeAllocationLayout";
import {
  formatTimesheetKmPrefetchCaption,
  getOptedInParticipantTravel,
  getParticipantDisplayName,
  resolveTravelItemSelection,
  toTravelItemSelectOption,
} from "./v5BillingUtils";

/**
 * V5 advance-shift travel editors — only opted-in participants (apply_km).
 * Prefills KM from worker timesheet totals ÷ opted-in participants when unset.
 */
const V5ParticipantTravelPanel = ({
  shiftDetails,
  rows,
  onChange,
  isDisabled = false,
}) => {
  const optedInRows = useMemo(
    () =>
      (Array.isArray(rows) ? rows : getOptedInParticipantTravel(shiftDetails)).filter(
        (row) => row?.apply_km ?? row?.km_for_invoicing
      ),
    [rows, shiftDetails]
  );

  const prefetchCaption = useMemo(
    () => formatTimesheetKmPrefetchCaption(optedInRows),
    [optedInRows]
  );

  if (!optedInRows.length) return null;

  const updateRow = (participantId, patch) => {
    if (!onChange || !Array.isArray(rows)) return;
    onChange(
      rows.map((row) =>
        String(row.participant_id) === String(participantId)
          ? { ...row, ...patch, km_prefetched: false }
          : row
      )
    );
  };

  return (
    <TimesheetCodeAllocationSectionCard
      title="Travel & KM"
      subtitle="Only participants opted in for KM invoicing"
      className="ca-travel-section"
    >
      {prefetchCaption ? (
        <p className="ca-google-km-label mb-3">{prefetchCaption}</p>
      ) : null}
      <div className="ca-travel-scroll space-y-4" data-timesheet-scroll>
        {optedInRows.map((row) => (
          <V5ParticipantTravelRow
            key={row.participant_id ?? row.participant_uuid}
            row={row}
            isDisabled={isDisabled}
            onUpdate={(patch) => updateRow(row.participant_id, patch)}
          />
        ))}
      </div>
    </TimesheetCodeAllocationSectionCard>
  );
};

const V5ParticipantTravelRow = ({ row, isDisabled, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(true);

  const travelOptions = useMemo(() => {
    const items = Array.isArray(row?.travel_items) ? row.travel_items : [];
    const options = items.map(toTravelItemSelectOption).filter(Boolean);
    const suggested = row?.suggested_travel_item
      ? toTravelItemSelectOption(row.suggested_travel_item)
      : null;
    if (
      suggested &&
      !options.some((o) => String(o.value) === String(suggested.value))
    ) {
      return [suggested, ...options];
    }
    return options;
  }, [row?.travel_items, row?.suggested_travel_item]);

  const selectedItem = useMemo(() => {
    const resolved = resolveTravelItemSelection(row);
    return toTravelItemSelectOption(resolved);
  }, [row]);

  const itemNumber = selectedItem?.support_item_number || "";
  const cataloguePrice =
    selectedItem?.price != null && selectedItem?.price !== ""
      ? selectedItem.price
      : null;

  const rateOptions = useMemo(() => {
    const options = [
      {
        label: "Apply Alternative Rate",
        value: "alternate",
      },
    ];
    if (cataloguePrice != null) {
      options.push({
        label: String(cataloguePrice),
        value: cataloguePrice,
      });
    }
    return options;
  }, [cataloguePrice]);

  const selectedRateOption = useMemo(() => {
    if (row?.km_alternative_rate) {
      return rateOptions[0] ?? null;
    }
    if (cataloguePrice == null) return null;
    return (
      rateOptions.find((opt) => String(opt.value) === String(cataloguePrice)) ??
      null
    );
  }, [row?.km_alternative_rate, cataloguePrice, rateOptions]);

  const participantLabel =
    getParticipantDisplayName(row) ||
    row?.participant_name ||
    `Participant #${row?.participant_id}`;

  const kmSummary =
    row?.km != null && row?.km !== "" ? `${row.km} KM` : "No KM";

  return (
    <div
      className={`ca-travel-subsection rounded-lg border border-slate-200/80 bg-white/80 p-3${
        isOpen ? " is-open" : ""
      }`}
    >
      <button
        type="button"
        className={`ca-travel-subsection-toggle ${isOpen ? "is-open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <div className="ca-travel-subsection-toggle-text">
          <p className="ca-travel-subsection-title mb-0">{participantLabel}</p>
          <span className="ca-travel-subsection-meta">{kmSummary}</span>
        </div>
        <HiOutlineChevronDown
          className={`ca-breakdown-chevron ${isOpen ? "is-open" : ""}`}
          aria-hidden
        />
      </button>

      {isOpen ? (
        <div className="ca-fields-row w-full mt-2">
          <div className="min-w-0 w-full">
            <label className="ca-field-label--dark">KM</label>
            <InputField
              cssClass="mt-0 h-40-important text-gray-700"
              inputWidth="w-full"
              isDisabled={isDisabled}
              backgroundColor="#F3F4F6"
              type="text"
              value={row?.km ?? ""}
              name={`v5_travel_km_${row.participant_id}`}
              handleChange={(value) => {
                onUpdate({ km: value === "" ? null : value });
              }}
            />
          </div>
          <div className="min-w-0 w-full">
            <label className="ca-field-label--dark">Travel Line Code</label>
            <SelectOption
              useMenuPortal={true}
              data={travelOptions}
              selectedOption={selectedItem}
              handleChange={(option) => {
                onUpdate({
                  km_item_id: option?.value ?? null,
                  km_price: option?.price ?? null,
                  km_alternative_rate: false,
                });
              }}
              isDisabled={isDisabled}
            />
          </div>
          <div className="min-w-0 w-full">
            <label className="ca-field-label--dark">Item Number</label>
            <InputField
              cssClass="mt-0 h-40-important text-gray-700"
              inputWidth="w-full"
              isDisabled={true}
              backgroundColor="#F3F4F6"
              type="text"
              value={itemNumber}
              name={`v5_travel_item_number_${row.participant_id}`}
              isRequired={false}
            />
          </div>
          <div className="min-w-0 w-full">
            <label className="ca-field-label--dark">Item Rate</label>
            <SelectOption
              useMenuPortal={true}
              data={rateOptions}
              selectedOption={selectedRateOption}
              handleChange={(option) => {
                if (option?.value === "alternate") {
                  onUpdate({
                    km_alternative_rate: true,
                    km_price:
                      row?.km_price != null && row?.km_price !== ""
                        ? row.km_price
                        : cataloguePrice,
                  });
                  return;
                }
                onUpdate({
                  km_alternative_rate: false,
                  km_price: option?.value ?? cataloguePrice ?? null,
                });
              }}
              isDisabled={isDisabled || !selectedItem}
            />
          </div>
          {row?.km_alternative_rate ? (
            <div className="min-w-0 w-full">
              <label className="ca-field-label--dark">Alternate Rate</label>
              <InputField
                cssClass="mt-0 h-40-important text-gray-700"
                inputWidth="w-full"
                isDisabled={isDisabled}
                backgroundColor="#F3F4F6"
                type="number"
                step="0.01"
                min="0"
                value={row?.km_price ?? ""}
                name={`v5_travel_alt_rate_${row.participant_id}`}
                handleChange={(value) => {
                  onUpdate({
                    km_alternative_rate: true,
                    km_price: value === "" ? null : value,
                  });
                }}
                isRequired={false}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default V5ParticipantTravelPanel;
